import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Row, Col, Card, message, Radio, Spin, Table, Space, Modal, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getProducts } from '../../../core/api/products';
import { getAllSuppliers } from '../../../core/api/settings';
import { createVariableCost, getAllVariableCosts, updateVariableCost, deleteVariableCost } from '../../../core/api/mediaplan';

const { Option } = Select;

const VariableCostsForm = ({ selectedBrand }) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [variableCosts, setVariableCosts] = useState([]);
  const [editingCost, setEditingCost] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();

  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
    fetchVariableCosts();
  }, [selectedBrand]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await getProducts();
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await getAllSuppliers();
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      message.error('Failed to fetch suppliers');
    }
  };

  const fetchVariableCosts = async () => {
    setTableLoading(true);
    try {
      const response = await getAllVariableCosts();
      setVariableCosts(response.data);
    } catch (error) {
      console.error('Error fetching variable costs:', error);
      message.error('Failed to fetch variable costs');
    } finally {
      setTableLoading(false);
    }
  };
  
  const handleEdit = (record) => {
    setEditingCost(record);
    editForm.setFieldsValue({
      product: record.product,
      supplier: record.supplier,
      merchant_fee: record.merchant_fee,
      merchant_fee_value_type: record.merchant_fee_value_type,
      call_center: record.call_center,
      call_center_value_type: record.call_center_value_type,
      shipping: record.shipping,
      shipping_value_type: record.shipping_value_type,
      fulfillment: record.fulfillment,
      fulfillment_value_type: record.fulfillment_value_type,
      royalty: record.royalty,
      royalty_value_type: record.royalty_value_type,
      refund: record.refund,
      refund_value_type: record.refund_value_type,
      product_cost: record.product_cost,
      product_cost_value_type: record.product_cost_value_type,
      mail_processing_cost: record.mail_processing_cost,
      mail_processing_cost_value_type: record.mail_processing_cost_value_type
    });
    setIsEditModalVisible(true);
  };
  
  const handleDelete = async (id) => {
    try {
      await deleteVariableCost(id);
      message.success('Variable cost deleted successfully');
      fetchVariableCosts();
 
    } catch (error) {
      console.error('Error deleting variable cost:', error);
      message.error('Failed to delete variable cost');
    } finally {
      setTableLoading(false);
    }
  };
  
  const handleEditSubmit = async (values) => {
    setFormLoading(true);
    try {
      await updateVariableCost(editingCost.id, values);
      message.success('Variable cost updated successfully');
      setIsEditModalVisible(false);
      fetchVariableCosts();
  
    } catch (error) {
      console.error('Error updating variable cost:', error);
      message.error('Failed to update variable cost');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setFormLoading(true);
    try {
      await createVariableCost(values);
      message.success('Variable cost added successfully');
      form.resetFields();
      // Call parent callback if provided
 
      fetchVariableCosts();
    } catch (error) {
      console.error('Error adding variable cost:', error);
      message.error('Failed to add variable cost');
    } finally {
      setFormLoading(false);
    }
  };

  const isProductAlreadyConfigured = (productId) => {
    return variableCosts.some(cost => cost.product === productId);
  };

  const filterOption = (input, option) => {
    return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  // Define table columns for variable costs
  const columns = [
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      sorter: (a, b) => a.product_name.localeCompare(b.product_name)
    },
    {
      title: 'Product Cost',
      dataIndex: 'product_cost',
      key: 'product_cost',
      render: (text, record) => `${parseFloat(text).toFixed(2)} ${record.product_cost_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Mail Processing',
      dataIndex: 'mail_processing_cost',
      key: 'mail_processing_cost',
      render: (text, record) => `${parseFloat(text).toFixed(2)} ${record.mail_processing_cost_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Merchant Fee',
      dataIndex: 'merchant_fee',
      key: 'merchant_fee',
      render: (text, record) => `${parseFloat(text).toFixed(2)} ${record.merchant_fee_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Call Center',
      dataIndex: 'call_center',
      key: 'call_center',
      render: (text, record) => `${parseFloat(text).toFixed(2)} ${record.call_center_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Shipping',
      dataIndex: 'shipping',
      key: 'shipping',
      render: (text, record) => `${parseFloat(text).toFixed(2)} ${record.shipping_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Fulfillment',
      dataIndex: 'fulfillment',
      key: 'fulfillment',
      render: (text, record) => `${parseFloat(text).toFixed(2)} ${record.fulfillment_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Royalty',
      dataIndex: 'royalty',
      key: 'royalty',
      render: (text, record) => `${parseFloat(text).toFixed(2)} ${record.royalty_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Refund',
      dataIndex: 'refund',
      key: 'refund',
      render: (text, record) => `${parseFloat(text).toFixed(2)} ${record.refund_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this variable cost?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              type="primary" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card 
      title="Variable Costs Per Order" 
      className="form-card"
      style={{ marginTop: '20px', marginBottom: '20px' }}
    >
      <Spin spinning={formLoading}>
        <Form
          form={form}
          layout="vertical"
          size="small"
          onFinish={handleSubmit}
          onSubmit={(e) => {
            e.preventDefault();      
            form.submit();          
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Product"
                name="product"
                rules={[
                  { required: true, message: 'Please select a product' },
                  {
                    validator: (_, value) => {
                      if (value && isProductAlreadyConfigured(value)) {
                        return Promise.reject('This product already has variable costs configured');
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Select
                  placeholder="Select product"
                  loading={loading}
                  showSearch
                  filterOption={filterOption}
                >
                  {products.map(product => (
                    <Option key={product.id} value={product.id}>{product.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
 
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Merchant Fee"
                name="merchant_fee"
                rules={[{ required: true, message: 'Please enter merchant fee' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="merchant_fee_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Call Center"
                name="call_center"
                rules={[{ required: true, message: 'Please enter call center cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="call_center_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Shipping"
                name="shipping"
                rules={[{ required: true, message: 'Please enter shipping cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="shipping_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Fulfillment"
                name="fulfillment"
                rules={[{ required: true, message: 'Please enter fulfillment cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="fulfillment_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Royalty"
                name="royalty"
                rules={[{ required: true, message: 'Please enter royalty cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="royalty_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Refund"
                name="refund"
                rules={[{ required: true, message: 'Please enter refund cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="refund_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Product Cost"
                name="product_cost"
                rules={[{ required: true, message: 'Please enter product cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="product_cost_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Mail Processing"
                name="mail_processing_cost"
                rules={[{ required: true, message: 'Please enter mail processing cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="mail_processing_cost_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              icon={<PlusOutlined />}
              loading={formLoading}
            >
              Add Variable Cost
            </Button>
          </Form.Item>
        </Form>
      </Spin>
      
      <div style={{ marginTop: '20px' }}>
        <Table
          columns={columns}
          dataSource={variableCosts}
          rowKey="id"
          loading={tableLoading}
          pagination={false}
          size="small"
          scroll={{ y: 300 }}
        />
      </div>
      
      <Modal
        title="Edit Variable Cost"
        visible={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Spin spinning={formLoading}>
          <Form
            form={editForm}
            layout="vertical"
            size="small"
            onFinish={handleEditSubmit}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Product"
                  name="product"
                  rules={[{ required: true, message: 'Please select a product' }]}
                >
                  <Select
                    placeholder="Select product"
                    loading={loading}
                    showSearch
                    filterOption={filterOption}
                    disabled
                  >
                    {products.map(product => (
                      <Option key={product.id} value={product.id}>{product.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
 
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Merchant Fee"
                  name="merchant_fee"
                  rules={[{ required: true, message: 'Please enter merchant fee' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="merchant_fee_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Call Center"
                  name="call_center"
                  rules={[{ required: true, message: 'Please enter call center cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="call_center_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Shipping"
                  name="shipping"
                  rules={[{ required: true, message: 'Please enter shipping cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="shipping_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Fulfillment"
                  name="fulfillment"
                  rules={[{ required: true, message: 'Please enter fulfillment cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="fulfillment_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Royalty"
                  name="royalty"
                  rules={[{ required: true, message: 'Please enter royalty cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="royalty_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Refund"
                  name="refund"
                  rules={[{ required: true, message: 'Please enter refund cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="refund_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Product Cost"
                  name="product_cost"
                  rules={[{ required: true, message: 'Please enter product cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="product_cost_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Mail Processing"
                  name="mail_processing_cost"
                  rules={[{ required: true, message: 'Please enter mail processing cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="mail_processing_cost_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={formLoading}
              >
                Update Variable Cost
              </Button>
              <Button 
                style={{ marginLeft: 8 }} 
                onClick={() => setIsEditModalVisible(false)}
              >
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </Card>
  );
};

export default VariableCostsForm;
